/**
 * Topics Service
 *
 * Service for fetching topics from the public REST API.
 * Used by the chatbot widget to display topic suggestions.
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
/**
 * Get API configuration from WordPress globals
 */
const getApiConfig = () => {
    const globals = window.wooAiChatbot ?? window.wooAIChatbot;
    return {
        restUrl: '/wp-json',
        nonce: globals?.nonce || '',
    };
};
/**
 * Generic fetch wrapper for API calls
 */
const fetchAPI = async (endpoint, options = {}) => {
    const { restUrl, nonce } = getApiConfig();
    const url = `${restUrl}${endpoint}`;
    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': nonce,
                ...options.headers,
            },
        });
        if (!response.ok) {
            const error = (await response.json().catch(() => ({
                message: `HTTP error! status: ${response.status}`,
                code: response.status.toString(),
            })));
            throw new Error(error.message || 'Request failed');
        }
        return response.json();
    }
    catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred');
    }
};
/**
 * Topics Service Class
 *
 * Handles all topic-related API operations for the chatbot widget.
 */
class TopicsService {
    constructor() {
        Object.defineProperty(this, "baseEndpoint", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '/woo-ai/v1/public/topics'
        });
        /**
         * Local cache for topics
         */
        Object.defineProperty(this, "topicsCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "cacheTimestamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "cacheTTL", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 5 * 60 * 1000
        }); // 5 minutes
    }
    /**
     * Fetch all enabled topics
     *
     * @returns Promise with array of enabled topics
     */
    async fetchTopics() {
        // Check cache first
        if (this.topicsCache && Date.now() - this.cacheTimestamp < this.cacheTTL) {
            return this.topicsCache;
        }
        try {
            const topics = await fetchAPI(this.baseEndpoint, {
                method: 'GET',
            });
            // Update cache
            this.topicsCache = topics;
            this.cacheTimestamp = Date.now();
            return topics;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch topics: ${error.message}`);
            }
            throw new Error('Failed to fetch topics: Unknown error');
        }
    }
    /**
     * Fetch contextual topics based on current page context
     *
     * @param context - Page context for filtering
     * @returns Promise with contextual topics response
     */
    async fetchContextualTopics(context) {
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/contextual`, {
                method: 'POST',
                body: JSON.stringify({
                    page_type: context.pageType || '',
                    product_id: context.productId || 0,
                    category_id: context.categoryId || 0,
                    utm_source: context.utmSource || '',
                    utm_campaign: context.utmCampaign || '',
                }),
            });
            return response;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch contextual topics: ${error.message}`);
            }
            throw new Error('Failed to fetch contextual topics: Unknown error');
        }
    }
    /**
     * Get topic by slug
     *
     * @param slug - Topic slug
     * @returns Promise with topic or null if not found
     */
    async getTopicBySlug(slug) {
        try {
            const topics = await this.fetchTopics();
            return topics.find((topic) => topic.slug === slug) || null;
        }
        catch {
            return null;
        }
    }
    /**
     * Get topics matching keywords
     *
     * @param keywords - Keywords to match
     * @returns Promise with matching topics
     */
    async getTopicsByKeywords(keywords) {
        try {
            const topics = await this.fetchTopics();
            const normalizedKeywords = keywords.map((k) => k.toLowerCase());
            return topics.filter((topic) => {
                const topicKeywords = (topic.keywords || []).map((k) => k.toLowerCase());
                return normalizedKeywords.some((keyword) => topicKeywords.some((tk) => tk.includes(keyword) || keyword.includes(tk)));
            });
        }
        catch {
            return [];
        }
    }
    /**
     * Clear topics cache
     */
    clearCache() {
        this.topicsCache = null;
        this.cacheTimestamp = 0;
    }
    /**
     * Get current page context from DOM
     *
     * Automatically detects page type, product ID, category ID, and UTM parameters.
     *
     * @returns TopicContext with detected values
     */
    getCurrentContext() {
        const context = {};
        // Detect page type from body classes
        const bodyClasses = document.body.className;
        if (bodyClasses.includes('single-product')) {
            context.pageType = 'product';
        }
        else if (bodyClasses.includes('woocommerce-cart')) {
            context.pageType = 'cart';
        }
        else if (bodyClasses.includes('woocommerce-checkout')) {
            context.pageType = 'checkout';
        }
        else if (bodyClasses.includes('tax-product_cat') ||
            bodyClasses.includes('woocommerce-shop')) {
            context.pageType = 'category';
        }
        else if (bodyClasses.includes('woocommerce-account') ||
            bodyClasses.includes('woocommerce-my-account')) {
            context.pageType = 'account';
        }
        else if (bodyClasses.includes('home') ||
            bodyClasses.includes('front-page')) {
            context.pageType = 'home';
        }
        // Get product ID from global or data attribute
        const globals = window.wooAiChatbot ?? window.wooAIChatbot;
        if (globals?.productId) {
            context.productId = Number(globals.productId);
        }
        // Get category ID from global or data attribute
        if (globals?.categoryId) {
            context.categoryId = Number(globals.categoryId);
        }
        // Get UTM parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get('utm_source');
        const utmCampaign = urlParams.get('utm_campaign');
        if (utmSource) {
            context.utmSource = utmSource;
        }
        if (utmCampaign) {
            context.utmCampaign = utmCampaign;
        }
        return context;
    }
    /**
     * Fetch topics with auto-detected context
     *
     * Convenience method that detects context automatically.
     *
     * @returns Promise with contextual topics response
     */
    async fetchTopicsWithContext() {
        const context = this.getCurrentContext();
        return this.fetchContextualTopics(context);
    }
}
// Export singleton instance
const topicsService = new TopicsService();
export default topicsService;
// Export class for testing
export { TopicsService };
